import { dateTimeFormatter } from "./date";
import {
  CONSENT_EVENT_TYPES,
  CONSENT_STATUS_TYPES,
  CONSENT_RENDERING_TYPES,
  CONSENT_DONE_BY_TYPES
} from "../constant/consent";

/**
 * E-Consent Helper Utilities
 * Shared utilities for e-consent functionality across components
 */

/**
 * Formats a consent event timestamp with date and time
 * Maps a consent event type to its corresponding status type
 * This is the single source of truth for event-to-status mapping
 * @param {string} eventType - The consent event type from CONSENT_EVENT_TYPES
 * @returns {string} The corresponding status type from CONSENT_STATUS_TYPES
 */
export const mapEventTypeToStatus = (eventType) => {
  const statusMap = {
    [CONSENT_EVENT_TYPES.CONSENT_ACCEPTED]: CONSENT_STATUS_TYPES.RECEIVED,
    [CONSENT_EVENT_TYPES.CONSENT_REVOKED]: CONSENT_STATUS_TYPES.REVOKED,
    [CONSENT_EVENT_TYPES.CONSENT_EXPIRED]: CONSENT_STATUS_TYPES.EXPIRED,
    [CONSENT_EVENT_TYPES.LINK_SENT]: CONSENT_STATUS_TYPES.PENDING,
    [CONSENT_EVENT_TYPES.LINK_EXPIRED]: CONSENT_STATUS_TYPES.LINK_EXPIRED,
    [CONSENT_EVENT_TYPES.LINK_REVOKED]: CONSENT_STATUS_TYPES.LINK_EXPIRED
  };

  return statusMap[eventType] || CONSENT_STATUS_TYPES.NO_RECORD;
};

// Map backend event type to frontend rendering type
export const mapServerEventTypeToDisplayRenderingType = (event) => {
  switch (event.type) {
    case CONSENT_EVENT_TYPES.CONSENT_ACCEPTED:
      // Distinguish electronic vs manual consent
      return event.doneBy === CONSENT_DONE_BY_TYPES.ECONSENT
        ? CONSENT_RENDERING_TYPES.CONSENT_RECEIVED_ELECTRONICALLY
        : CONSENT_RENDERING_TYPES.CONSENT_RECORDED_MANUALLY;

    case CONSENT_EVENT_TYPES.LINK_SENT:
      return CONSENT_RENDERING_TYPES.LINK_SENT;

    case CONSENT_EVENT_TYPES.LINK_REVOKED:
      return CONSENT_RENDERING_TYPES.LINK_REVOKED;

    case CONSENT_EVENT_TYPES.LINK_EXPIRED:
      return CONSENT_RENDERING_TYPES.LINK_EXPIRED;

    case CONSENT_EVENT_TYPES.CONSENT_REVOKED:
      return CONSENT_RENDERING_TYPES.CONSENT_REVOKED;

    case CONSENT_EVENT_TYPES.CONSENT_EXPIRED:
      return CONSENT_RENDERING_TYPES.CONSENT_EXPIRED;

    default:
      return CONSENT_RENDERING_TYPES.NO_RECORD;
  }
};

/**
 * Formats a consent event timestamp with date and time
 * Used in consent history drawer to show detailed timestamps
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted timestamp string (e.g., "12/31/24 14:30")
 */
export const formatConsentEventTimestamp = (date) => {
  if (!date) return "MM/DD/YY";

  return dateTimeFormatter(date, "MM/DD/YY HH:mm");
};

/**
 * Formats a consent event date without time
 * Used in patient header tooltips for cleaner display
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string (e.g., "12/31/24")
 */
export const formatConsentEventDate = (date) => {
  if (!date) return "MM/DD/YY";

  return dateTimeFormatter(date, "MM/DD/YY");
};

export const getConsentActionUserName = (event, t) => {
  if (!event) return t("consent.system");

  return event.doneBy === CONSENT_DONE_BY_TYPES.ECONSENT ? event.patientName : event.userName;
};
